import { useInfiniteQuery, useQueries } from '@tanstack/react-query';
import { creatorbaseQueries } from '@round/api';
import uniq from 'lodash/uniq';
import { PurchaseOrderRow, PurchaseOrderRowAllocation } from './helpers';
import { useBrandsQueriesByIds, useProjectsQueriesByIds } from '@round/creatorbase-helpers/api-hooks';

const options = creatorbaseQueries.purchaseOrders.options.infinite;
const teamsOptions = creatorbaseQueries.team.options.list;
const usersOptions = creatorbaseQueries.user.options.list;
const allocationOptions = creatorbaseQueries.purchaseOrderLines.options.listAll;
const campaignOptions = creatorbaseQueries.campaigns.options.list;

type Options = Pick<ReturnType<typeof options>, 'enabled' | 'refetchOnWindowFocus'>;
type Params = Parameters<typeof options>[0];

export default function usePurchaseOrders(params: Params = {}, queryOptions?: Options) {
    const query = useInfiniteQuery({ ...options(params), ...queryOptions });
    const teamsQueries = useQueries({
        queries:
            query.data?.pages.map((page) => {
                const ids = uniq(page.results.map((po) => po.team_id));
                return {
                    ...teamsOptions({ id: ids.toString(), page_size: ids.length, page: 1 }),
                    enabled: ids.length > 0,
                };
            }) ?? [],
    });

    const usersQueries = useQueries({
        queries:
            query.data?.pages.map((page) => {
                const ids = uniq(page.results.map((po) => po.created_by));
                return {
                    ...usersOptions({ id: ids.toString(), page_size: ids.length, page: 1 }),
                    enabled: ids.length > 0,
                };
            }) ?? [],
    });

    const purchaseOrderIdsWithAllocations =
        query.data?.pages.map((page) =>
            page.results.filter((po) => po.remaining_amount < po.amount).map((po) => po.id)
        ) ?? [];

    const allocationQueries = useQueries({
        queries: purchaseOrderIdsWithAllocations.map((poIds) => {
            return {
                ...allocationOptions({
                    purchase_order_id: poIds.toString(),
                }),
                enabled: poIds.length > 0,
            };
        }),
    });

    const campaignIds = allocationQueries
        .map((query) => query.data?.map((line) => line.campaign_id) ?? [])
        .map((ids) => uniq(ids));

    const campaignQueries = useQueries({
        queries: campaignIds.map((ids) => {
            return {
                ...campaignOptions({ id: ids.toString(), page_size: ids.length }),
                enabled: ids.length > 0,
            };
        }),
    });

    const projectIds = campaignQueries.map((query) => query.data?.results.map((c) => c.project_id) ?? []).map(uniq);
    const projectQueries = useProjectsQueriesByIds(projectIds);

    const brandIds = projectQueries.map((query) => query.data?.results.map((p) => p.brand_id) ?? []).map(uniq);
    const brandQueries = useBrandsQueriesByIds(brandIds);

    const rows =
        query.data?.pages.flatMap<PurchaseOrderRow>((page, pageIndex) => {
            return page.results.map((po) => {
                const allocations =
                    allocationQueries[pageIndex]?.data?.filter((line) => line.purchase_order_id === po.id) ?? [];

                return {
                    ...po,
                    team: teamsQueries[pageIndex]?.data?.results.find((team) => team.id === po.team_id) ?? null,
                    createdBy: usersQueries[pageIndex]?.data?.results.find((user) => user.id === po.created_by) ?? null,
                    allocations: allocations.map((allocation) => {
                        const campaign =
                            campaignQueries[pageIndex]?.data?.results.find((c) => allocation.campaign_id === c.id) ??
                            null;
                        const project =
                            projectQueries[pageIndex]?.data?.results.find((p) => p.id === campaign?.project_id) ?? null;
                        const brand =
                            brandQueries[pageIndex]?.data?.results.find((b) => b.id === project?.brand_id) ?? null;

                        return { ...allocation, campaign, project, brand };
                    }),
                };
            });
        }) ?? [];

    const getIsTeamLoading = (po: PurchaseOrderRow) => {
        const poPageIndex = query.data?.pages.findIndex((page) => !!page.results.find((p) => p.id === po.id));
        const teamsQuery = typeof poPageIndex === 'number' ? teamsQueries[poPageIndex] : null;
        return Boolean(teamsQuery?.isPending && teamsQuery.isFetching);
    };

    const getIsUserLoading = (po: PurchaseOrderRow) => {
        const poPageIndex = query.data?.pages.findIndex((page) => !!page.results.find((p) => p.id === po.id));
        const usersQuery = typeof poPageIndex === 'number' ? usersQueries[poPageIndex] : null;
        return Boolean(usersQuery?.isPending && usersQuery.isFetching);
    };

    const getIsAllocationsLoading = (po: PurchaseOrderRow) => {
        const poPageIndex = query.data?.pages.findIndex((page) => !!page.results.find((p) => p.id === po.id));
        const allocationsQuery = typeof poPageIndex === 'number' ? allocationQueries[poPageIndex] : null;
        return Boolean(allocationsQuery?.isLoading);
    };

    const getIsCampaignLoading = (allocation: PurchaseOrderRowAllocation) => {
        const poPageIndex = query.data?.pages.findIndex(
            (page) => !!page.results.find((p) => p.id === allocation.purchase_order_id)
        );
        const campaignQuery = typeof poPageIndex === 'number' ? campaignQueries[poPageIndex] : null;
        return Boolean(campaignQuery?.isLoading);
    };

    const getIsBrandLoading = (allocation: PurchaseOrderRowAllocation) => {
        const poPageIndex = query.data?.pages.findIndex(
            (page) => !!page.results.find((p) => p.id === allocation.purchase_order_id)
        );
        const brandQuery = typeof poPageIndex === 'number' ? brandQueries[poPageIndex] : null;
        return Boolean(brandQuery?.isLoading);
    };

    const getIsProjectLoading = (allocation: PurchaseOrderRowAllocation) => {
        const poPageIndex = query.data?.pages.findIndex(
            (page) => !!page.results.find((p) => p.id === allocation.purchase_order_id)
        );
        const projectQuery = typeof poPageIndex === 'number' ? projectQueries[poPageIndex] : null;
        return Boolean(projectQuery?.isLoading);
    };

    return {
        rows,
        isLoading: query.isFetching && query.isPending,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: query.hasNextPage,
        fetchNextPage: query.fetchNextPage,
        status: query.status,
        getIsTeamLoading,
        getIsUserLoading,
        getIsAllocationsLoading,
        getIsCampaignLoading,
        getIsBrandLoading,
        getIsProjectLoading,
        refetch: query.refetch,
    };
}
